import React, { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import { routes, OCCURRENCES_GATE, type RouteConfig } from "./routes";
import ProtectedRoute from "../components/ProtectedRoute";
import PublicRoute from "../components/PublicRoute";
import ProtectedShell from "../components/ui/ProtectedShell";

const OccurrencesPage                = lazy(() => import("../pages/Occurrences"));
const PrivacidadePage                = lazy(() => import("../pages/Privacidade"));
const SystemIssuesPage               = lazy(() => import("../pages/SystemIssues"));
const NotFoundPage                   = lazy(() => import("../pages/NotFound"));

function ProtectedPage({ route }: { route: RouteConfig }) {
  const Component = route.component;

  return (
    <ProtectedRoute
      minRank={route.minRank}
      requiredCaps={route.requiredCaps}
      requiredPermissions={route.requiredPermissions}
      permissionMode={route.permissionMode}
      requiredCapabilities={route.requiredCapabilities}
      features={route.requiredFeatures}
      allowReadOnly={route.allowReadOnly}
      fallbackPath="/access-denied"
    >
      <Component />
    </ProtectedRoute>
  );
}

const AppRouter: React.FC = () => {
  const protectedRoutes = routes.filter((r) => r.isProtected);
  const publicRoutes = routes.filter((r) => !r.isProtected);

  return (
    <Routes>
      {/* Aviso de Privacidade — PÚBLICO e ABERTO: acessível logado ou não, sem
          redirect. Por isso fica fora do <PublicRoute> (que manda o logado embora)
          e do <ProtectedRoute>. */}
      <Route
        path="/privacidade"
        element={
          <Suspense fallback={null}>
            <PrivacidadePage />
          </Suspense>
        }
      />

      {/* Módulo de Ocorrências — protegido, layout próprio (sem shell padrão).
          Rota única com wildcard para evitar unmount/remount entre
          `/occurrences`, `/occurrences/:processId` e `/occurrences/:processId/:subjectId`.
          O componente parseia os segmentos via useLocation. */}
      <Route
        element={
          <ProtectedRoute
            fallbackPath="/login"
            requiredCapabilities={OCCURRENCES_GATE.requiredCapabilities}
            features={OCCURRENCES_GATE.requiredFeatures}
            allowReadOnly
          >
            <Suspense fallback={null}>
              <OccurrencesPage />
            </Suspense>
          </ProtectedRoute>
        }
        path="/occurrences/*"
      />

      {/* Públicas (internas) */}
      {publicRoutes.map((route) => {
        const Component = route.component;
        return (
          <Route
            key={route.path}
            path={route.path}
            element={
              route.handlesOwnAuthRedirect ? (
                <Component />
              ) : (
                <PublicRoute>
                  <Component />
                </PublicRoute>
              )
            }
          />
        );
      })}

      {/* Pai protegido — só autenticação; o confinamento de leitura é por
          rota filha (cada uma declara `allowReadOnly`). */}
      <Route
        element={
          <ProtectedRoute fallbackPath="/login" allowReadOnly>
            <ProtectedShell />
          </ProtectedRoute>
        }
      >
        {protectedRoutes.map((route) => {
          if (route.children) {
            return (
              <Route
                key={route.path}
                path={route.path + '/*'}
                element={<ProtectedPage route={route} />}
              >
                {route.children.map((child) => {
                  const isIndex = child.path === route.path
                  const relativePath = isIndex
                    ? undefined
                    : child.path.startsWith(route.path + '/')
                      ? child.path.slice(route.path.length + 1)
                      : child.path
                  return (
                    <Route
                      key={child.path}
                      index={isIndex}
                      path={relativePath}
                      element={<ProtectedPage route={child} />}
                    />
                  )
                })}
              </Route>
            )
          }
          return (
            <Route key={route.path} path={route.path} element={<ProtectedPage route={route} />} />
          )
        })}

        {/* Módulo de Problemas Reportados — dentro do shell padrão (sidebar +
            topbar), ao contrário de Ocorrências acima. Rota única com
            wildcard (mesmo motivo do Ocorrências: evita unmount/remount
            entre `/system-issues`, `/system-issues/:id`,
            `/system-issues/triage[...]`) — o componente parseia os
            segmentos via useLocation. Gate só por `can_report_system_issues`
            (sem feature-flag por CC); sem `allowReadOnly`, quem não tem
            `can_edit_own_records` (cliente, Visualizador) segue fora, como
            quando o confinamento morava no shell. A fila de triagem é gated
            dentro do componente (`pages/SystemIssues.tsx`), não aqui — a
            rota raiz é compartilhada entre reporter e triagem. */}
        <Route
          path="system-issues/*"
          element={
            <ProtectedRoute requiredCaps="can_report_system_issues">
              <Suspense fallback={null}>
                <SystemIssuesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

      </Route>

      {/* Catch-all: 404 em tela cheia, fora do shell (e fora do ProtectedRoute —
          rota inexistente é 404 mesmo sem sessão, não redireciona pro login). */}
      <Route
        path="*"
        element={
          <Suspense fallback={null}>
            <NotFoundPage />
          </Suspense>
        }
      />
    </Routes>
  );
};

export default AppRouter;